import {
  Button,
  Combobox,
  HelpText,
  Label,
  TextField,
  Toggle,
} from '@lego/connect-components-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ENABLE_FORM_PREVIEW } from '../../constants/feature-flags';
import type { FormFieldState, FormState, FormVariableState } from '../../types/types';
import { FormVariableEditor } from './FormVariableEditor';
import { InfoTip, labelRowClass } from './formVariableUtils';

// ─── Shared field wrapper ─────────────────────────────────────────────────────

type ComboboxFieldProps = {
  label: string;
  options: { value: string; label: string }[];
  value: string | string[];
  onChange: (values: string[]) => void;
  placeholder: string;
  disabled?: boolean;
  multiple?: boolean;
  required?: boolean;
  error?: boolean;
  helpText?: string;
  portalTarget?: Element | null;
};

const ComboboxField = ({
  label,
  options,
  value,
  onChange,
  placeholder,
  disabled,
  multiple,
  required,
  error,
  helpText,
  portalTarget,
}: ComboboxFieldProps) => {
  const asArray = Array.isArray(value) ? value : value ? [value] : [];
  return (
    <div className="flex-1">
      <Label>
        {label}
        {required && <span className="text-core-red-1000 ml-50">*</span>}
      </Label>
      <Combobox
        fullWidth
        size="medium"
        value={asArray}
        onChange={(_e, items) => onChange(items.map((i) => i.value))}
        placeholder={placeholder}
        disabled={disabled}
        multiple={multiple}
        required={required}
        invalid={error}
        {...(portalTarget ? { portalTarget } : {})}
      >
        {options.map((o) => (
          <Combobox.Option key={o.value} value={o.value}>
            {o.label}
          </Combobox.Option>
        ))}
      </Combobox>
      {helpText && <HelpText variant="negative">{helpText}</HelpText>}
    </div>
  );
};

// ─── Action Buttons (exported for Modal.Footer) ───────────────────────────────

export const SaveButton = ({
  isEditing,
  isSubmitting,
  onSave,
}: {
  isEditing: boolean;
  isSubmitting: boolean;
  onSave: () => void;
}) => {
  const { t } = useTranslation();
  return (
    <Button
      variant="primary"
      size="medium"
      onClick={onSave}
      disabled={isSubmitting}
      loading={isSubmitting}
      loadingText={isEditing ? t('formPage.action.updating') : t('formPage.action.creating')}
    >
      {isEditing ? t('formPage.action.saveChanges') : t('formPage.action.createForm')}
    </Button>
  );
};

export type FormBuilderActionsProps = {
  isEditing: boolean;
  isSubmitting: boolean;
  isPreviewValidating: boolean;
  hasUnsavedChanges: boolean;
  onSave: () => void;
  onPreview: () => void;
};

export const FormBuilderActions = ({
  isEditing,
  isSubmitting,
  isPreviewValidating,
  hasUnsavedChanges,
  onSave,
  onPreview,
}: FormBuilderActionsProps) => {
  const { t } = useTranslation();
  const showPreview = ENABLE_FORM_PREVIEW && !isSubmitting && (!isEditing || hasUnsavedChanges);

  return (
    <div className="flex items-center gap-200">
      {showPreview && (
        <Button
          variant="accent"
          size="medium"
          onClick={onPreview}
          disabled={isPreviewValidating}
          loading={isPreviewValidating}
          loadingText={t('formPage.action.validatingPreview')}
        >
          {t('formPage.action.previewForm')}
        </Button>
      )}
      <SaveButton isEditing={isEditing} isSubmitting={isSubmitting} onSave={onSave} />
    </div>
  );
};

// ─── Variables Section ────────────────────────────────────────────────────────

const hasVariablesToEdit = (fields: FormFieldState[], variables?: FormVariableState[]) =>
  fields.length > 0 || !!variables?.length;

type VariablesSectionProps = {
  variables: FormVariableState[];
  fields: FormFieldState[];
  submitAttempted: boolean;
  onVariableChange: (id: string, value: Record<string, unknown>) => void;
  onRemoveVariable: (id: string) => void;
  onAddVariable: (field: FormFieldState) => void;
  portalTarget?: Element | null;
};

const VariablesSection = ({
  variables,
  fields,
  submitAttempted,
  onVariableChange,
  onRemoveVariable,
  onAddVariable,
  portalTarget,
}: VariablesSectionProps) => {
  const { t } = useTranslation();

  return (
    <div className="flex flex-col gap-200">
      <Label>{t('formPage.display.labelVariablesConfig')}</Label>
      <FormVariableEditor
        variables={variables}
        fields={fields}
        submitAttempted={submitAttempted}
        onVariableChange={onVariableChange}
        onRemoveVariable={onRemoveVariable}
        onAddVariable={onAddVariable}
        portalTarget={portalTarget}
      />
    </div>
  );
};

// ─── Main Editor ──────────────────────────────────────────────────────────────

export type FormBuilderProps = {
  formState: Partial<FormState>;
  isEditing: boolean;
  submitAttempted: boolean;

  // Groups
  groupOptions: { value: string; label: string }[];
  onGroupChange: (values: string[]) => void;

  // Options
  labelOptions: { value: string; label: string }[];
  labelRevisionOptions: { value: string; label: string }[];
  printerOptions: { value: string; label: string }[];
  selectedPrinterValues: string[];

  // Handlers
  onFieldChange: (updates: Partial<FormState>) => void;
  onPrinterChange: (values: string[]) => void;
  onVariableChange: (id: string, value: Record<string, unknown>) => void;
  onRemoveVariable: (id: string) => void;
  onAddVariable: (field: FormFieldState) => void;
};

const FormBuilder = ({
  formState,
  isEditing,
  submitAttempted,
  groupOptions,
  onGroupChange,
  labelOptions,
  labelRevisionOptions,
  printerOptions,
  selectedPrinterValues,
  onFieldChange,
  onPrinterChange,
  onVariableChange,
  onRemoveVariable,
  onAddVariable,
}: FormBuilderProps) => {
  const { t } = useTranslation();
  const [portalTarget, setPortalTarget] = useState<HTMLDivElement | null>(null);
  const { name, labelId, labelRevisions, variables, fields, groupIds, showLabelPreview } =
    formState;
  const availableFields: FormFieldState[] = fields ?? [];
  const requiredError = t('formPage.variableEditor.fieldRequired', 'This field is required');
  const showNameError = submitAttempted && !name?.trim();
  const showLabelError = submitAttempted && !labelId;
  const showRevisionsError = submitAttempted && !labelRevisions?.length;
  const showVariables = hasVariablesToEdit(availableFields, variables);

  return (
    <div ref={setPortalTarget} className="flex flex-col gap-300">
      {/* Form Name */}
      <div>
        <Label>
          {t('formPage.display.formName')}
          <span className="text-core-red-1000 ml-50">*</span>
        </Label>
        <TextField
          size="medium"
          placeholder={t('formPage.display.enterFormName')}
          value={name ?? ''}
          onChange={(_e, value) => onFieldChange({ name: value })}
          aria-label={t('formPage.display.formName')}
          required
          validationState={showNameError ? 'invalid' : undefined}
        />
        {showNameError && (
          <HelpText variant="negative" className="mt-100">
            {requiredError}
          </HelpText>
        )}
      </div>

      {/* Label + Revisions */}
      <div className="grid grid-cols-1 gap-200 md:grid-cols-2">
        <ComboboxField
          label={t('formPage.display.label')}
          options={labelOptions}
          value={labelId ? labelId.toString() : ''}
          onChange={(values) =>
            onFieldChange({ labelId: values[0] ? Number(values[0]) : null, labelRevisions: [] })
          }
          placeholder={t('formPage.display.selectLabel')}
          disabled={isEditing}
          required
          error={showLabelError}
          helpText={showLabelError ? requiredError : undefined}
          portalTarget={portalTarget}
        />
        <ComboboxField
          label={t('formPage.display.revisions')}
          options={labelRevisionOptions}
          value={labelRevisions ?? []}
          onChange={(values) => onFieldChange({ labelRevisions: values })}
          placeholder={t('formPage.display.selectRevisions')}
          disabled={!labelId}
          multiple
          required
          error={showRevisionsError}
          helpText={showRevisionsError ? requiredError : undefined}
          portalTarget={portalTarget}
        />
      </div>

      {/* Printers */}
      <ComboboxField
        label={t('formPage.display.printerList')}
        options={printerOptions}
        value={selectedPrinterValues}
        onChange={onPrinterChange}
        placeholder={t('formPage.display.selectPrinters')}
        multiple
        portalTarget={portalTarget}
      />

      {/* Groups */}
      <ComboboxField
        label={t('formPage.display.groups', 'Groups')}
        options={[
          { value: '\x00__all__', label: t('formPage.display.chooseAll', 'Choose all') },
          ...groupOptions,
        ]}
        value={(groupIds ?? []).map(String)}
        onChange={onGroupChange}
        placeholder={t('formPage.display.selectGroups', 'Select groups')}
        multiple
        portalTarget={portalTarget}
      />

      {/* Label preview visibility */}
      <div>
        <div className={labelRowClass}>
          <Label>{t('formPage.display.showLabelPreview')}</Label>
          <InfoTip text={t('formPage.display.showLabelPreviewHelp')} />
        </div>
        <Toggle
          aria-label={t('formPage.display.showLabelPreview')}
          size="small"
          checked={showLabelPreview ?? true}
          onChange={(e) => onFieldChange({ showLabelPreview: e.target.checked })}
        />
      </div>

      {/* Variables */}
      {showVariables && (
        <VariablesSection
          variables={variables ?? []}
          fields={availableFields}
          submitAttempted={submitAttempted}
          onVariableChange={onVariableChange}
          onRemoveVariable={onRemoveVariable}
          onAddVariable={onAddVariable}
          portalTarget={portalTarget}
        />
      )}
    </div>
  );
};

export default FormBuilder;
